import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Mail, MessageSquare, AlertTriangle, DollarSign } from "lucide-react";
import { UsageSummary } from "@/hooks/useApiUsage";
import { formatCurrency } from "@/lib/formatters";

interface UsageSummaryCardsProps {
  summary: UsageSummary[];
  isLoading?: boolean;
}

const SERVICE_CONFIG: Record<string, { label: string; icon: typeof Mail }> = {
  resend: { label: "Sähköposti (Resend)", icon: Mail },
  "46elks": { label: "SMS (46elks)", icon: MessageSquare },
};

export function UsageSummaryCards({ summary, isLoading }: UsageSummaryCardsProps) {
  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader className="pb-2">
              <div className="h-4 bg-muted rounded w-24" />
            </CardHeader>
            <CardContent>
              <div className="h-8 bg-muted rounded w-16 mb-2" />
              <div className="h-2 bg-muted rounded w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  // Calculate totals
  const totalCost = summary.reduce((sum, s) => sum + (s.monthly_cost || 0), 0);
  const totalRateLimited = summary.reduce((sum, s) => sum + (s.rate_limited_count || 0), 0);

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      {/* Per-service cards */}
      {summary.map((service) => {
        const config = SERVICE_CONFIG[service.service] || {
          label: service.service,
          icon: Mail,
        };
        const Icon = config.icon;

        const monthlyPercentage = service.monthly_limit
          ? Math.min((service.monthly_count / service.monthly_limit) * 100, 100)
          : 0;

        const isWarning = monthlyPercentage >= (service.warning_threshold || 0.8) * 100;
        const isCritical = monthlyPercentage >= 100;

        return (
          <Card
            key={service.service}
            className={
              isCritical
                ? "border-destructive bg-destructive/5"
                : isWarning
                ? "border-orange-500/50 bg-orange-500/5"
                : ""
            }
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{config.label}</CardTitle>
              <Icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {service.monthly_count}
                {service.monthly_limit && (
                  <span className="text-sm font-normal text-muted-foreground">
                    {" "}
                    / {service.monthly_limit}
                  </span>
                )}
              </div>
              {service.monthly_limit && (
                <Progress
                  value={monthlyPercentage}
                  className={`h-2 mt-2 ${
                    isCritical
                      ? "[&>div]:bg-destructive"
                      : isWarning
                      ? "[&>div]:bg-orange-500"
                      : ""
                  }`}
                />
              )}
              <p className="text-xs text-muted-foreground mt-1">
                Tänään: {service.daily_count}
                {service.daily_limit && ` / ${service.daily_limit}`}
              </p>
            </CardContent>
          </Card>
        );
      })}

      {/* Total cost card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Kustannukset (kk)</CardTitle>
          <DollarSign className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{formatCurrency(totalCost)}</div>
          <p className="text-xs text-muted-foreground mt-1">Arvioitu kokonaiskustannus</p>
        </CardContent>
      </Card>

      {/* Rate limited card */}
      {totalRateLimited > 0 && (
        <Card className="border-orange-500/50 bg-orange-500/5">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Estetyt pyynnöt</CardTitle>
            <AlertTriangle className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{totalRateLimited}</div>
            <p className="text-xs text-muted-foreground mt-1">Rate limit -ylitykset tässä kuussa</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
